import { FC, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DividerWithText } from '@farmshare/ui-components';
import type {
  CutsheetCatalog,
  CutsheetQuestionnaire,
  ItemPricing,
  Specification,
} from '../../types';
import { formatPricing } from '../../utils/pricing';
import type {
  ItemSpecValues,
  ResponseCutOption,
  ResponseSpecificationSelection,
  ResponseTrimSelections,
} from './types';
import {
  type QuestionAnswer,
  useQuestionnaireResponse,
} from './questionnaire-response-context';
import { getAvailableSaveTypes, getOptionValue } from './wizard-utils';

// ── Pricing resolution helpers ───────────────────────────────────────────────

/**
 * Resolves effective pricing for a catalog item (cut/trim/extra), preferring
 * the questionnaire-level override over the item's own pricing.
 */
const resolveItemPricing = (
  itemId: string,
  defaultPricing: ItemPricing | undefined,
  pricingOverrides: CutsheetQuestionnaire['pricingOverrides'],
): ItemPricing | undefined => pricingOverrides?.[itemId] ?? defaultPricing;

/**
 * Resolves effective pricing for a single spec option value. Priority order:
 *   1. `questionnaire.specOptionPricingOverrides[specId][value]`
 *   2. `option.pricing` on a matching rich SpecificationOption
 *   3. `spec.pricingAdjustments[value]`
 */
const resolveSpecOptionPricing = (
  spec: Specification,
  optionValue: string,
  specOptionPricingOverrides: CutsheetQuestionnaire['specOptionPricingOverrides'],
): ItemPricing | undefined => {
  const override = specOptionPricingOverrides?.[spec.id]?.[optionValue];
  if (override) return override;

  if (Array.isArray(spec.options)) {
    for (const opt of spec.options) {
      if (typeof opt === 'string') continue;
      if (getOptionValue(opt) === optionValue && opt.pricing)
        return opt.pricing;
    }
  }

  return spec.pricingAdjustments?.[optionValue];
};

interface ReviewStepProps {
  questionnaire: CutsheetQuestionnaire;
  catalog: CutsheetCatalog;
  answers: Record<string, QuestionAnswer>;
  itemSpecValues: ItemSpecValues;
  subSelections: Record<string, string[]>;
  onNavigateToQuestion: (index: number) => void;
}

// ── Pricing summary ──────────────────────────────────────────────────────────

/**
 * PricingSummary — shows pricing for every selected cut, trim, and priced
 * specification choice. Respects questionnaire-level pricing overrides for both
 * catalog items and spec options.
 * @param root0
 * @param root0.catalog
 * @param root0.questionnaire
 * @param root0.selectedCuts
 * @param root0.trimSelections
 * @param root0.specifications
 */
const PricingSummary: FC<{
  catalog: CutsheetCatalog;
  questionnaire: CutsheetQuestionnaire;
  selectedCuts: ResponseCutOption[];
  trimSelections: ResponseTrimSelections | undefined;
  specifications: ResponseSpecificationSelection[];
}> = ({
  catalog,
  questionnaire,
  selectedCuts,
  trimSelections,
  specifications,
}) => {
  const pricedItems: Array<{ name: string; pricing: ItemPricing }> = [];
  const { pricingOverrides, specOptionPricingOverrides } = questionnaire;

  /** Emits priced-spec-option line items for a given response specifications array. */
  const pushSpecPricing = (
    contextLabel: string,
    specs: ResponseSpecificationSelection[] | undefined,
  ) => {
    for (const specSel of specs ?? []) {
      const spec = catalog.specifications.find(
        (s) => s.id === specSel.specification,
      );
      if (!spec) continue;
      const values = Array.isArray(specSel.value)
        ? specSel.value
        : [String(specSel.value ?? '')];
      for (const val of values) {
        if (!val) continue;
        const pricing = resolveSpecOptionPricing(
          spec,
          val,
          specOptionPricingOverrides,
        );
        if (pricing) {
          pricedItems.push({
            name: contextLabel
              ? `${contextLabel} — ${spec.title}: ${val}`
              : `${spec.title}: ${val}`,
            pricing,
          });
        }
      }
    }
  };

  // Selected cuts (with their per-cut priced specs)
  for (const cut of selectedCuts) {
    const cutDef = catalog.cuts.find((c) => c.id === cut.optionId);
    if (!cutDef) continue;
    const pricing = resolveItemPricing(
      cut.optionId,
      cutDef.pricing,
      pricingOverrides,
    );
    if (pricing) pricedItems.push({ name: cutDef.title, pricing });
    pushSpecPricing(cutDef.title, cut.specifications);
  }

  // Selected trims (with their per-trim priced specs), in rank order
  for (const trim of trimSelections?.trim ?? []) {
    const trimDef = catalog.trim.find((t) => t.id === trim.optionId);
    if (!trimDef) continue;
    const pricing = resolveItemPricing(
      trim.optionId,
      trimDef.pricing,
      pricingOverrides,
    );
    if (pricing) pricedItems.push({ name: trimDef.title, pricing });
    pushSpecPricing(trimDef.title, trim.specifications);
  }

  // Global/standalone specification answers
  pushSpecPricing('', specifications);

  return (
    <Box sx={{ mt: 2 }}>
      <DividerWithText text="Pricing Summary" align="left" />
      {/* <Card variant="outlined" sx={{ p: 2, mt: 1 }}> */}
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Stack spacing={1.5}>
          {pricedItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pricing information available for selected items.
            </Typography>
          ) : (
            pricedItems.map((item, idx) => (
              <Stack
                key={idx}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">{item.name}</Typography>
                <Typography variant="body2" color="primary" fontWeight="medium">
                  {formatPricing(item.pricing)}
                </Typography>
              </Stack>
            ))
          )}

          <Divider />
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ fontSize: '0.7rem' }}
          >
            Final pricing is calculated on actual weights and specifications.
          </Typography>
        </Stack>
      </CardContent>
      {/* </Card> */}
    </Box>
  );
};

/**
 * The final review step of the preview dialog — shows a summary of every
 * answered question and a pricing breakdown for items that have pricing.
 * @param root0
 * @param root0.itemSpecValues
 */
export const ReviewStep: FC<ReviewStepProps> = ({ itemSpecValues }) => {
  const {
    questionnaire,
    catalog,
    selectedCuts,
    trimSelections,
    specifications,
    cutsheetName,
    cutsheetSaveType,
    isProcessor,
    producerId,
    buyerId,
    fixedCutsheetType,
    handleCutsheetNameChange,
    handleCutsheetSaveTypeChange,
  } = useQuestionnaireResponse();

  const availableSaveTypes = useMemo(
    () => getAvailableSaveTypes({ isProcessor, producerId, buyerId }),
    [isProcessor, producerId, buyerId],
  );
  const hideSaveTypeChoice =
    !!fixedCutsheetType || availableSaveTypes.length <= 1;

  return (
    <Stack spacing={3}>
      {/* ── Naming section ─────────────────────────────────────────────── */}
      <Box>
        <DividerWithText text="Name Your Cutsheet" align="left" />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, mt: 1 }}
        >
          Give your cutsheet a memorable name and choose how to save it.
        </Typography>

        <TextField
          fullWidth
          label="Cutsheet Name"
          placeholder="e.g., My Standard Beef Cutsheet"
          value={cutsheetName}
          onChange={(e) => handleCutsheetNameChange(e.target.value)}
          variant="outlined"
          required
          sx={{ mb: 3 }}
        />

        {/* Save-type selection — hidden when a fixed type is provided or only
            one option is available (context auto-selects the sole option). */}
        {!hideSaveTypeChoice && (
          <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
            <DividerWithText text="Save Cutsheet To" align="left" />
            <RadioGroup
              value={cutsheetSaveType}
              sx={{ gap: 1 }}
              onChange={(e) =>
                handleCutsheetSaveTypeChange(
                  e.target.value as 'user' | 'vendor' | 'producer' | 'buyer',
                )
              }
            >
              {isProcessor && (
                <>
                  <FormControlLabel
                    value="vendor"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">House Cutsheet</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Save as a house cutsheet available to all customers
                        </Typography>
                      </Box>
                    }
                  />
                  {producerId && (
                    <FormControlLabel
                      value="producer"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">
                            Producer Cutsheet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Save against the producer for future use for them
                            and their customers
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                </>
              )}
              {buyerId && (
                <FormControlLabel
                  value="buyer"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">Buyer Cutsheet</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Save against the buyer for their future use
                      </Typography>
                    </Box>
                  }
                />
              )}
              {!isProcessor && (
                <FormControlLabel
                  value="user"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">My Cutsheets</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Save to your personal custom cutsheets
                      </Typography>
                    </Box>
                  }
                />
              )}
            </RadioGroup>
          </FormControl>
        )}
      </Box>

      {/* ── Pricing summary ─────────────────────────────────────────────── */}
      <Box sx={{ mt: 2 }}>
        {/* <Card variant="outlined" sx={{ p: 2, mt: 1 }}> */}
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <PricingSummary
            catalog={catalog}
            questionnaire={questionnaire}
            selectedCuts={selectedCuts}
            trimSelections={trimSelections}
            specifications={specifications}
          />
        </CardContent>
        {/* </Card> */}
      </Box>
    </Stack>
  );
};
